import React, { useState, useRef, useEffect, useCallback, forwardRef } from 'react';

import { Flex, Box, Tooltip } from '@chakra-ui/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { closeCreateKRPanelAction, openCreateKRPanelAction } from 'actions/createKRPanelActions';
import { openObjectiveDetailsPanelActionCreator } from 'actions/objectiveDetailsPanelActions';
import ObjectiveSidePanel from 'components/ObjectiveSidePanel';
import TagIcon from 'components/ObjectiveTagList/TagIcon';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { setClickThroughView } from 'reducers/okrsPageSlice';
import ObjectiveProgressBar from 'screens/Team/Rabbit/Goals/Audit/ObjectiveProgressBar';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { VerticallyAlignedFlexContainer } from 'styles/common';
import { themeColors } from 'themes/clients/baseTheme';

import {
  AddNew,
  ChevronDown,
  Goals,
  ObjectiveIcon,
  RightArrowCircleIcon,
  ViewMore,
} from 'assets/assetsV1/iconComponents';

import { GOAL_PROGRESS_OPTIONS_MAP, OBJECTIVE_LEVEL_TAGS } from 'constants/ProjectConstants';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendOpenedObjectivePanelEvent,
} from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showSuccessSnackbar } from 'services/snackbar';

import { Typography12 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { onObjectiveModificationSuccess } from '../../../../analytics/goals/GoalObjective/components/ObjectiveAndGoalsList';
import { OKRMenuOptions } from '../OKRMenuOptions';

const horizontalLineWidth = 16;
const iconWidth = 16;
const iconLeftPadding = 8;
/* left margin of icon (this is left padding of icon for now) + half the width of icon (16 / 2)+ width of horizontal line (16) */
const firstLevelComponentLeftMargin = iconLeftPadding + iconWidth / 2 + horizontalLineWidth;
// currently this is left padding of icon
const zeroLevelComponentLeftPadding = 0;

/* left margin of icon (this is left padding of icon for now) + half the width of icon (16 / 2) */
const firstVerticalLineLeftMargin = iconLeftPadding + iconWidth / 2;

export const getLeftMarginForComponent = (level) =>
  `${zeroLevelComponentLeftPadding + level * firstLevelComponentLeftMargin}px`;
export const RabbitObjectiveDisplayComponent = ({
  uuid,
  value,
  level = 0,
  expandRow = () => {},
  collapseRow = () => {},
  isLastElement,
  levelsForWhichLinesToBeDrawn,
  isExpanded = false,
  timeboundDetails,
}) => {
  /* component is still partially controlled as on page change the states don't reset
   and the icons on next page rows are according to the states of previous page's rows
   */
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const isExpandedDerived = isExpanded; // partially controlled component, will have to do complete re-write for it to be a complete controlled component
  // show realign objective or not

  const SubTypeIcon = () => (
    <VerticallyAlignedFlexContainer>
      <TagIcon tagId={timeboundDetails?.objectiveLevel ? timeboundDetails?.objectiveLevel : undefined} />
      <Typography12 customColor={theme.palette.text.dark1} leftGutter={1}>
        {OBJECTIVE_LEVEL_TAGS.find(({ value }) => value === timeboundDetails?.objectiveLevel)?.label}
      </Typography12>
    </VerticallyAlignedFlexContainer>
  );

  const handleObjectiveExpansionCollapse = (ev) => {
    ev.stopPropagation();
    if (isExpandedDerived) collapseRow(uuid);
    else expandRow(uuid);
  };

  // realign hover state
  // keeping them here cause if hovered on the hover button, the parent's hover state turns false

  return (
    <Flex
      alignItems="flex-start"
      justifyContent="initial"
      flexWrap="nowrap"
      ml={getLeftMarginForComponent(level)}
      p="16px"
      pl="0px"
    >
      {isExpandedDerived && <DownLine level={level + 1} />}
      {
        // to draw vertical lines only for required levels
      }
      {levelsForWhichLinesToBeDrawn.map((_level) => (
        <TLine half={_level === level && isLastElement} noHorizontal={_level < level} key={_level} level={_level} />
      ))}
      {
        // the following line is for current level vertical line and horizontal line
      }
      {level > 0 && <TLine half={isLastElement} level={level} />}

      <Flex p="8px" onClick={handleObjectiveExpansionCollapse} cursor="pointer">
        <RightArrowCircleIcon fill="none" color={themeColors.button.blue.default} boxSize="16px" />
      </Flex>

      <Tooltip label={objectivesName}>
        <Flex p="4px">
          <ObjectiveIcon color={themeColors.functional['7651BC']} boxSize="24px" />
        </Flex>
      </Tooltip>

      <Flex direction="column" flex="1 1 auto">
        <div>
          <Tooltip label={value?.length > 120 ? value : ''}>
            <Flex alignItems="flex-start">
              <Flex flexDirection="column" flex="1 1 auto">
                <TextBodyMedium
                  className="highlight-able-text"
                  style={{
                    marginTop: '4px',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    webkitLineClamp: '2',
                    webkitBoxOrient: 'vertical',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }}
                >
                  {value}
                </TextBodyMedium>
                <SubTypeIcon />
              </Flex>
            </Flex>
          </Tooltip>
        </div>
      </Flex>
    </Flex>
  );
};

const RabbitAggregatedMetricsSecondaryActions = forwardRef((props, ref) => {
  const { setMenuIsOpen, viewDetailsClickHandler, allowObjectiveGoalCreation } = props;
  return (
    <Flex alignItems="center" justifyContent="flex-end">
      <Button
        variant="rowButtonOutlined"
        StartIcon={ViewMore}
        mr="24px"
        onClick={viewDetailsClickHandler}
        startIconProps={{ fill: 'transparent' }}
      >
        View Details
      </Button>
      {allowObjectiveGoalCreation && (
        <span
          ref={ref}
          onClick={(ev) => {
            ev.stopPropagation();
            setMenuIsOpen((prev) => !prev);
          }}
        >
          <ClickAwayListener onClickAway={() => setMenuIsOpen(false)}>
            <Button variant="tertiary" StartIcon={AddNew}>
              Add New
            </Button>
          </ClickAwayListener>
        </span>
      )}
    </Flex>
  );
});
export const RabbitObjectiveTableAggregatedMetricsComponent = (props) => {
  const { value } = props;

  const { barDistribution, hoverDetails, overall } = value;

  const getLabelText = () => {
    if (hoverDetails) {
      const arrayOfCount = Object?.keys(hoverDetails)
        ?.sort((a, b) => GOAL_PROGRESS_OPTIONS_MAP[a]?.order - GOAL_PROGRESS_OPTIONS_MAP[b]?.order)
        ?.map((hoverDetail) => {
          if (hoverDetail && hoverDetails[hoverDetail]) {
            return `${GOAL_PROGRESS_OPTIONS_MAP[hoverDetail].label}: ${hoverDetails[hoverDetail]?.count}`;
          }
        })
        ?.filter((f) => f);

      return arrayOfCount?.reduce(
        (curr, prev, index) => curr + prev + (index === arrayOfCount?.length - 1 ? '' : ' • '),
        ''
      );
    }
  };

  return (
    <AbsolutePositionContainer>
      <Box w="100%" h="100%" p="16px" pl="0px">
        <ObjectiveProgressBar
          data={barDistribution}
          percentageValue={Math.round(parseFloat(overall?.avgCompletionPercentage))}
        />
        {/* TODO: Following component is to be replaced with rabbit version */}
        <Box textAlign="left">
          <Typography12 customColor={theme.palette.text.dark1}>{getLabelText()}</Typography12>
        </Box>
      </Box>
    </AbsolutePositionContainer>
  );
};

export const RabbitObjectiveRowSecondaryActions = (props) => {
  const {
    postCardId,
    objectiveId,
    value,
    expandRow = () => {},
    refetchData = () => {},
    currentPageNumber,
    expandedRowIds,
    fetchObjectiveData,
    fetchGoalsData,
    rows,
    updateRowDataModifiedForArray,
    addNewObjective,
    selectedTimeBoundPeriod,
    isTeamHubView,
    timeboundDetails,
    udpateObjectiveName,
    allowObjectiveGoalCreation = true,
  } = props;

  const dispatch = useDispatch();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  // menu options state
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const anchorRef = useRef();

  const employee = useSelector(selectLoggedInEmployeeData);

  // objective panel state
  const [isOpenCreateObjectivePanel, setIsOpenCreateObjectivePanel] = useState(false);

  // timebound details of newly created objective
  const [timeboundDetailsOfCreatedObjective, setTimeboundDetailsOfCreatedObjective] = useState(null);

  const [isGoalPanelOfCreatedObjective, setIsGoalPanelOfCreatedObjective] = useState(false);

  const handleLevelChange = useCallback(() => {
    udpateObjectiveName(value);
  }, [value]);

  const functionRef = useRef();
  functionRef.current = handleLevelChange;

  const handleObjectiveNameClick = (postCardId) => {
    dispatch(
      openObjectiveDetailsPanelActionCreator({
        objectivePId: postCardId,
        onNameChange: (objectiveName) => {
          udpateObjectiveName(objectiveName);
        },
        onDelete: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar(`${objectivesName} deleted!`));
        },
        onLevelChange: () => {
          if (typeof functionRef.current === 'function') {
            functionRef.current();
          }
        },
        onReAlign: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar(`${objectivesName} realigned!`));
        },
      })
    );

    sendOpenedObjectivePanelEvent({
      eventProperties: {
        source: 'Insights Page Overall',
      },
    });
  };

  useEffect(() => {
    if (isGoalPanelOfCreatedObjective && timeboundDetailsOfCreatedObjective) {
      const closeKRPanel = () => dispatch(closeCreateKRPanelAction());

      dispatch(
        openCreateKRPanelAction({
          onClose: closeKRPanel,
          onCancel: closeKRPanel,
          onSave: () => {
            refetchData({
              pageNumber: currentPageNumber,
              onSuccess: () => {
                onObjectiveModificationSuccess({
                  expandedRowIds,
                  fetchObjectiveData,
                  fetchGoalsData,
                  rows,
                  updateRowDataModifiedForArray,
                });
              },
            });
          },
          selectedTimeBoundPeriod: timeboundDetails,
          ownerEmployee: employee,
          subObjective: timeboundDetailsOfCreatedObjective,
          actionDispatchSource: isTeamHubView ? 'Team Page' : 'Insights',
          panelHeaderText: isTeamHubView ? 'Add Key Result' : 'Add goal',
        })
      );
    }
  }, [timeboundDetailsOfCreatedObjective, isGoalPanelOfCreatedObjective]);

  return (
    <>
      {/* <RabbitAggregatedMetricsSecondaryActions
        ref={anchorRef}
        setMenuIsOpen={setMenuIsOpen}
        viewDetailsClickHandler={() => {
          handleObjectiveNameClick(objectiveId);
        }}
        allowObjectiveGoalCreation={allowObjectiveGoalCreation}
        {...props}
      /> */}
      <Flex alignItems="center" justifyContent="flex-end">
        <Button
          variant="rowButtonOutlined"
          StartIcon={ViewMore}
          mr="24px"
          onClick={(e) => {
            handleObjectiveNameClick(postCardId);
            e.stopPropagation();
          }}
          className="hovering"
          startIconProps={{ fill: 'transparent' }}
        >
          View Details
        </Button>
        {allowObjectiveGoalCreation && (
          <ClickAwayListener onClickAway={() => setMenuIsOpen(false)}>
            <span
              ref={anchorRef}
              onClick={(ev) => {
                ev.stopPropagation();
                setMenuIsOpen((prev) => !prev);
              }}
            >
              <Button variant="rowButtonOutlined" StartIcon={Goals} EndIcon={ChevronDown} pr="2px">
                Add
              </Button>
              <OKRMenuOptions
                addKR={() => {
                  sendClickedOnCreateKRevent({
                    eventProperties: {
                      source: isTeamHubView ? 'Team Page' : 'Insights Page',
                    },
                  });

                  const closeKRPanel = () => dispatch(closeCreateKRPanelAction());

                  dispatch(
                    openCreateKRPanelAction({
                      onClose: closeKRPanel,
                      onCancel: closeKRPanel,
                      onSave: () => {
                        refetchData({
                          pageNumber: currentPageNumber,
                          onSuccess: () => {
                            onObjectiveModificationSuccess({
                              expandedRowIds,
                              fetchObjectiveData,
                              fetchGoalsData,
                              rows,
                              updateRowDataModifiedForArray,
                            });
                          },
                        });
                      },
                      selectedTimeBoundPeriod: timeboundDetails,
                      ownerEmployee: employee,
                      subObjective: { ...timeboundDetails, postCardId },
                      actionDispatchSource: isTeamHubView ? 'Team Page' : 'Insights',
                      panelHeaderText: isTeamHubView ? 'Add Key Result' : 'Add goal',
                    })
                  );
                }}
                addObjective={() => {
                  expandRow(objectiveId);
                  sendClickedOnCreateObjectiveEvent({
                    eventProperties: {
                      source: isTeamHubView ? 'Team Page' : 'Insights Page',
                    },
                  });
                  setIsOpenCreateObjectivePanel(true);
                }}
                anchorEl={anchorRef}
                isOpen={menuIsOpen}
                setIsOpen={setMenuIsOpen}
                isSubObjectiveMenu
              />
            </span>
          </ClickAwayListener>
        )}
      </Flex>

      {isOpenCreateObjectivePanel && (
        <div onClick={(e) => e.stopPropagation()}>
          <ObjectiveSidePanel
            open={isOpenCreateObjectivePanel}
            onCancel={() => {
              setTimeboundDetailsOfCreatedObjective(null);
              setIsGoalPanelOfCreatedObjective(false);
              setIsOpenCreateObjectivePanel(false);
            }}
            uuid={timeboundDetails.objectiveId}
            selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            objectiveTimeBoundId={objectiveId}
            otbPostCardId={postCardId}
            addNewObjective={addNewObjective}
            rows={rows}
            updateRowDataModifiedForArray={updateRowDataModifiedForArray}
            setIsOpenCreateGoalPanel={(value) => {
              setIsGoalPanelOfCreatedObjective(value);
            }}
            setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
            source={isTeamHubView ? 'Team Page' : 'Insights'}
          />
        </div>
      )}
    </>
  );
};

export const RabbitInsightsViewObjectiveRowSecondaryActions = (props) => {
  const {
    postCardId,
    objectiveId,
    value,
    refetchData = () => {},
    currentPageNumber,
    expandedRowIds,
    fetchObjectiveData,
    fetchGoalsData,
    rows,
    updateRowDataModifiedForArray,
    udpateObjectiveName,
  } = props;

  const dispatch = useDispatch();

  const handleLevelChange = useCallback(() => {
    udpateObjectiveName(value);
  }, [value]);

  const functionRef = useRef();
  functionRef.current = handleLevelChange;

  const handleObjectiveNameClick = (postCardId) => {
    dispatch(
      openObjectiveDetailsPanelActionCreator({
        objectivePId: postCardId,
        onNameChange: (objectiveName) => {
          udpateObjectiveName(objectiveName);
        },
        onDelete: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar('Objective deleted!'));
        },
        onLevelChange: () => {
          if (typeof functionRef.current === 'function') {
            functionRef.current();
          }
        },
        onReAlign: () => {
          refetchData({
            pageNumber: currentPageNumber,
            onSuccess: () => {
              onObjectiveModificationSuccess({
                expandedRowIds,
                fetchObjectiveData,
                fetchGoalsData,
                rows,
                updateRowDataModifiedForArray,
              });
            },
          });
          dispatch(showSuccessSnackbar('Objective realigned!'));
        },
      })
    );

    sendOpenedObjectivePanelEvent({
      eventProperties: {
        source: 'Insights Page Overall',
      },
    });
  };

  const handleClickThrough = (row) => {
    dispatch(
      setClickThroughView({
        name: value,
        parentUuid: objectiveId,
      })
    );
  };

  return (
    <Flex alignItems="center" justifyContent="flex-end">
      <Button
        variant="rowButtonOutlined"
        StartIcon={ViewMore}
        mr="24px"
        onClick={(e) => {
          handleObjectiveNameClick(postCardId);
          e.stopPropagation();
        }}
        className="hovering"
        startIconProps={{ fill: 'transparent' }}
      >
        View Details
      </Button>
      <Button
        variant="rowButtonOutlined"
        StartIcon={ViewMore}
        onClick={(e) => {
          handleClickThrough();
          e.stopPropagation();
        }}
        startIconProps={{ fill: 'transparent' }}
      >
        View Insights
      </Button>
    </Flex>
  );
};

const AbsolutePositionContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const VerticalLine = styled.div`
  position: absolute;
  width: 1px;
  top: 0;
  bottom: 0;
  background: ${(props) => props.theme.colors.text.g3};
  ${(props) =>
    props.level &&
    css`
      left: ${getLeftPadding(props.level)};
    `};
  ${(props) =>
    props.half &&
    css`
      // 32px = 16(top padding container) + 8(top icon padding) + 8(icon height/ 2)
      bottom: ${`calc(100% - ${props.isGoal ? '36px' : '32px'})`};
    `};
`;

const HorizontalLine = styled.div`
  position: absolute;
  height: 1px;
  width: 16px;
  top: ${(props) =>
    props.isGoal ? '36px' : '32px'}; // 32px = 16(top padding container) + 8(top icon padding) + 8(icon height/ 2)
  background: ${(props) => props.theme.colors.text.g3};
  ${(props) =>
    props.level &&
    css`
      left: ${getLeftPadding(props.level)};
    `};
`;

export const TLine = ({ level, half, noHorizontal, isGoal = false }) => (
  <>
    <VerticalLine half={half} level={level} isGoal={isGoal} />
    {!noHorizontal && <HorizontalLine isGoal={isGoal} level={level} />}
  </>
);

export const DownLine = styled.div`
  position: absolute;
  width: 1px;
  top: 50%;
  bottom: 0;
  background: ${(props) => props.theme.colors.text.g3};
  ${(props) =>
    props.level &&
    css`
      left: ${getLeftPadding(props.level)};
    `};
`;

const getLeftPadding = (level) =>
  `${zeroLevelComponentLeftPadding + level * firstVerticalLineLeftMargin + (level - 1) * horizontalLineWidth}px`;
